import { formatRupiah, type LoyaltyRule, type PromoDef } from '@safepos/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  type Column,
  DataTable,
  Modal,
  PageHeader,
  SelectField,
  TextField,
} from '@safepos/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';
import { type ProductDto, useInvalidate } from './CatalogPages';

const when = (s: string | null) =>
  s ? new Date(s).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmt = (n: number) => new Intl.NumberFormat('id-ID').format(n);

// ============================ PELANGGAN ============================
interface Customer {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  note: string | null;
  pointsBalance: number;
  totalSpent: number;
  visitCount: number;
  lastVisitAt: string | null;
}
interface CustomerDetail extends Customer {
  ledger: {
    id: string;
    type: string;
    points: number;
    balanceAfter: number;
    note: string | null;
    createdAt: string;
  }[];
  orders: {
    id: string;
    orderNo: string;
    grandTotal: number;
    status: string;
    closedAt: string;
    pointsEarned: number;
    pointsRedeemed: number;
  }[];
}
const LEDGER_TEXT: Record<string, string> = {
  earn: 'Diperoleh',
  redeem: 'Ditukar',
  reverse_earn: 'Batal (perolehan)',
  reverse_redeem: 'Batal (tukar)',
  adjust: 'Penyesuaian',
};

export function CustomersPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const write = can('customers:write');
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: ['customers', q],
    queryFn: () =>
      api.get<{ items: Customer[]; nextCursor: string | null }>(
        `/customers?limit=100${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`,
      ),
  });
  const [form, setForm] = useState<Partial<Customer> | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ['customer', detailId],
    enabled: !!detailId,
    queryFn: () => api.get<CustomerDetail>(`/customers/${detailId}`),
  });
  const save = useSubmit(async () => {
    const body = {
      name: form!.name,
      phone: form!.phone || null,
      email: form!.email || null,
      note: form!.note || null,
    };
    if (form!.id) await api.put(`/customers/${form!.id}`, body);
    else await api.post('/customers', body);
    inv('customers', 'customer');
    setForm(null);
  });
  const del = useSubmit(async (c: Customer) => {
    if (!confirm(`Hapus pelanggan ${c.name}? Riwayat transaksinya tetap tersimpan.`)) return;
    await api.del(`/customers/${c.id}`);
    setDetailId(null);
    inv('customers');
  });
  const [adj, setAdj] = useState<{ points: string; note: string } | null>(null);
  const adjust = useSubmit(async () => {
    await api.post(`/customers/${detailId}/points`, {
      points: Math.round(Number(adj!.points)),
      note: adj!.note,
    });
    inv('customers', 'customer');
    setAdj(null);
  });

  const cols: Column<Customer>[] = [
    {
      header: 'Nama',
      cell: (c) => (
        <button className="font-medium text-brand-700 underline" onClick={() => setDetailId(c.id)}>
          {c.name}
        </button>
      ),
    },
    { header: 'Telepon', cell: (c) => c.phone ?? '—' },
    { header: 'Poin', cell: (c) => fmt(c.pointsBalance) },
    { header: 'Kunjungan', cell: (c) => c.visitCount },
    { header: 'Total belanja', cell: (c) => formatRupiah(c.totalSpent) },
    { header: 'Terakhir', cell: (c) => when(c.lastVisitAt) },
  ];

  return (
    <>
      <PageHeader
        title="Pelanggan"
        subtitle="Data pelanggan, saldo poin, dan riwayat belanja"
        actions={write && <Button onClick={() => setForm({ name: '' })}>+ Pelanggan</Button>}
      />
      <Card>
        <div className="mb-3 max-w-sm">
          <TextField
            label="Cari nama / telepon / email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <DataTable columns={cols} rows={list.data?.items ?? []} />
      </Card>

      <Modal
        open={!!form}
        title={form?.id ? 'Ubah pelanggan' : 'Tambah pelanggan'}
        onClose={() => setForm(null)}
      >
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            <TextField
              label="Nama"
              value={form.name ?? ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              minLength={2}
              autoFocus
            />
            <TextField
              label="Nomor telepon"
              inputMode="tel"
              value={form.phone ?? ''}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              label="Catatan"
              value={form.note ?? ''}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
            <div className="text-right">
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={!!detailId}
        title={detail.data?.name ?? 'Pelanggan'}
        onClose={() => setDetailId(null)}
      >
        {detail.data && (
          <div className="space-y-4 text-sm">
            {del.error && <Alert>{del.error}</Alert>}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-slate-500">Telepon</div>
                {detail.data.phone ?? '—'}
              </div>
              <div>
                <div className="text-xs text-slate-500">Email</div>
                {detail.data.email ?? '—'}
              </div>
              <div>
                <div className="text-xs text-slate-500">Saldo poin</div>
                <b>{fmt(detail.data.pointsBalance)}</b>
              </div>
              <div>
                <div className="text-xs text-slate-500">Total belanja</div>
                {formatRupiah(detail.data.totalSpent)} · {detail.data.visitCount}×
              </div>
            </div>
            {write && (
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setForm(detail.data!)}>
                  Ubah data
                </Button>
                <Button variant="secondary" onClick={() => setAdj({ points: '', note: '' })}>
                  Sesuaikan poin
                </Button>
                <Button variant="ghost" onClick={() => void del.run(detail.data!)}>
                  Hapus
                </Button>
              </div>
            )}
            <div>
              <div className="mb-1 font-medium">Riwayat poin</div>
              <ul className="max-h-48 divide-y overflow-auto rounded border">
                {detail.data.ledger.length === 0 && (
                  <li className="p-3 text-slate-500">Belum ada riwayat</li>
                )}
                {detail.data.ledger.map((e) => (
                  <li key={e.id} className="flex justify-between gap-2 px-3 py-2">
                    <span>
                      {LEDGER_TEXT[e.type] ?? e.type}
                      {e.note ? ` · ${e.note}` : ''}
                      <span className="block text-xs text-slate-500">{when(e.createdAt)}</span>
                    </span>
                    <span className={e.points < 0 ? 'text-red-600' : 'text-emerald-700'}>
                      {e.points > 0 ? '+' : ''}
                      {e.points}
                      <span className="block text-right text-xs text-slate-500">
                        saldo {e.balanceAfter}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="mb-1 font-medium">Transaksi terakhir</div>
              <ul className="max-h-40 divide-y overflow-auto rounded border">
                {detail.data.orders.length === 0 && (
                  <li className="p-3 text-slate-500">Belum ada transaksi</li>
                )}
                {detail.data.orders.map((o) => (
                  <li key={o.id} className="flex justify-between px-3 py-2">
                    <span>
                      {o.orderNo} {o.status === 'void' && <Badge tone="danger">Batal</Badge>}
                      <span className="block text-xs text-slate-500">{when(o.closedAt)}</span>
                    </span>
                    <span>{formatRupiah(o.grandTotal)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!adj} title="Sesuaikan poin" onClose={() => setAdj(null)}>
        {adj && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void adjust.run();
            }}
          >
            {adjust.error && <Alert>{adjust.error}</Alert>}
            <TextField
              label="Jumlah poin (negatif untuk mengurangi)"
              inputMode="numeric"
              value={adj.points}
              onChange={(e) => setAdj({ ...adj, points: e.target.value.replace(/[^\d-]/g, '') })}
              required
              autoFocus
            />
            <TextField
              label="Alasan"
              value={adj.note}
              minLength={3}
              maxLength={100}
              onChange={(e) => setAdj({ ...adj, note: e.target.value })}
              required
            />
            <div className="text-right">
              <Button type="submit" disabled={adjust.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ============================ PROMO ============================
const DAYS = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
const KIND_TEXT = { percent: 'Persen', amount: 'Nominal', bogo: 'Beli X gratis Y' } as const;
const SCOPE_TEXT = { order: 'Seluruh transaksi', product: 'Produk tertentu', category: 'Kategori' };

interface PromoForm {
  id?: string;
  name: string;
  kind: PromoDef['kind'];
  scope: PromoDef['scope'];
  targetIds: string[];
  percent: string;
  amount: string;
  buyQty: string;
  getQty: string;
  minSpend: string;
  maxDiscount: string;
  days: number[];
  timeFrom: string;
  timeTo: string;
  startsAt: string;
  endsAt: string;
  outletIds: string[];
  stackable: boolean;
  priority: string;
  requiresVoucher: boolean;
  active: boolean;
}
const blankPromo = (): PromoForm => ({
  name: '',
  kind: 'percent',
  scope: 'order',
  targetIds: [],
  percent: '10',
  amount: '',
  buyQty: '1',
  getQty: '1',
  minSpend: '',
  maxDiscount: '',
  days: [],
  timeFrom: '',
  timeTo: '',
  startsAt: '',
  endsAt: '',
  outletIds: [],
  stackable: false,
  priority: '100',
  requiresVoucher: false,
  active: true,
});
const toLocalInput = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromPromo = (p: PromoDef): PromoForm => ({
  id: p.id,
  name: p.name,
  kind: p.kind,
  scope: p.scope,
  targetIds: p.targetIds,
  percent: String(p.valueBp / 100),
  amount: p.valueAmount ? String(p.valueAmount) : '',
  buyQty: String(p.buyQty),
  getQty: String(p.getQty),
  minSpend: p.minSpend ? String(p.minSpend) : '',
  maxDiscount: p.maxDiscount ? String(p.maxDiscount) : '',
  days: p.days,
  timeFrom: p.timeFrom ?? '',
  timeTo: p.timeTo ?? '',
  startsAt: toLocalInput(p.startsAt),
  endsAt: toLocalInput(p.endsAt),
  outletIds: p.outletIds,
  stackable: p.stackable,
  priority: String(p.priority),
  requiresVoucher: p.requiresVoucher,
  active: p.active,
});
const toBody = (f: PromoForm) => ({
  name: f.name.trim(),
  kind: f.kind,
  scope: f.scope,
  targetIds: f.scope === 'order' ? [] : f.targetIds,
  valueBp: f.kind === 'percent' ? Math.round(Number(f.percent.replace(',', '.')) * 100) : 0,
  valueAmount: f.kind === 'amount' ? Math.round(Number(f.amount || 0)) : 0,
  buyQty: Math.max(1, Math.round(Number(f.buyQty || 1))),
  getQty: Math.max(1, Math.round(Number(f.getQty || 1))),
  minSpend: Math.round(Number(f.minSpend || 0)),
  maxDiscount: Math.round(Number(f.maxDiscount || 0)),
  days: f.days,
  timeFrom: f.timeFrom || null,
  timeTo: f.timeTo || null,
  startsAt: f.startsAt ? new Date(f.startsAt).toISOString() : null,
  endsAt: f.endsAt ? new Date(f.endsAt).toISOString() : null,
  outletIds: f.outletIds,
  stackable: f.stackable,
  priority: Math.round(Number(f.priority || 100)),
  requiresVoucher: f.requiresVoucher,
  active: f.active,
});
const describe = (p: PromoDef) => {
  const v =
    p.kind === 'percent'
      ? `${p.valueBp / 100}%`
      : p.kind === 'amount'
        ? formatRupiah(p.valueAmount)
        : `Beli ${p.buyQty} gratis ${p.getQty}`;
  return `${v} · ${SCOPE_TEXT[p.scope]}`;
};

export function PromotionsPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const write = can('promotions:write');
  const promos = useQuery({
    queryKey: ['promotions'],
    queryFn: () => api.get<PromoDef[]>('/promotions'),
  });
  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<{ id: string; name: string }[]>('/categories'),
  });
  const prods = useQuery({
    queryKey: ['products', 'options'],
    queryFn: () => api.get<{ data: ProductDto[] }>('/products?limit=200&active=true'),
  });
  const outlets = useQuery({
    queryKey: ['outlets'],
    queryFn: () => api.get<{ id: string; name: string }[]>('/outlets'),
  });
  const [form, setForm] = useState<PromoForm | null>(null);
  const save = useSubmit(async () => {
    const body = toBody(form!);
    if (form!.id) await api.put(`/promotions/${form!.id}`, body);
    else await api.post('/promotions', body);
    inv('promotions', 'vouchers');
    setForm(null);
  });
  const del = useSubmit(async (p: PromoDef) => {
    if (!confirm(`Hapus promo "${p.name}"? Voucher terkait ikut dinonaktifkan.`)) return;
    await api.del(`/promotions/${p.id}`);
    inv('promotions', 'vouchers');
  });
  const toggle = useSubmit(async (p: PromoDef) => {
    await api.put(`/promotions/${p.id}`, { ...p, active: !p.active });
    inv('promotions');
  });

  const targets =
    form?.scope === 'product'
      ? (prods.data?.data ?? []).map((p) => ({ id: p.id, name: p.name }))
      : (cats.data ?? []);
  const flip = <T,>(arr: T[], v: T) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const cols: Column<PromoDef>[] = [
    { header: 'Promo', cell: (p) => <span className="font-medium">{p.name}</span> },
    { header: 'Aturan', cell: (p) => describe(p) },
    {
      header: 'Syarat',
      cell: (p) => (
        <span className="text-xs text-slate-600">
          {[
            p.minSpend ? `min ${formatRupiah(p.minSpend)}` : null,
            p.days.length ? p.days.map((d) => DAYS[d]).join(' ') : null,
            p.timeFrom ? `${p.timeFrom}–${p.timeTo}` : null,
            p.endsAt ? `s.d. ${when(p.endsAt)}` : null,
            p.requiresVoucher ? 'butuh voucher' : null,
            p.stackable ? 'bisa ditumpuk' : 'eksklusif',
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      ),
    },
    { header: 'Prioritas', cell: (p) => p.priority },
    {
      header: 'Status',
      cell: (p) => (
        <Badge tone={p.active ? 'success' : 'neutral'}>{p.active ? 'Aktif' : 'Mati'}</Badge>
      ),
    },
    {
      header: '',
      className: 'text-right',
      cell: (p) =>
        write && (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setForm(fromPromo(p))}>
              Ubah
            </Button>
            <Button variant="ghost" onClick={() => void toggle.run(p)}>
              {p.active ? 'Matikan' : 'Aktifkan'}
            </Button>
            <Button variant="ghost" onClick={() => void del.run(p)}>
              Hapus
            </Button>
          </div>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Promo"
        subtitle="Diskon otomatis di kasir (juga berlaku saat offline). Promo khusus voucher hanya aktif bila kodenya dimasukkan."
        actions={write && <Button onClick={() => setForm(blankPromo())}>+ Promo</Button>}
      />
      {(del.error || toggle.error) && <Alert>{del.error ?? toggle.error}</Alert>}
      <Card>
        <DataTable columns={cols} rows={promos.data ?? []} />
      </Card>

      <Modal
        open={!!form}
        title={form?.id ? 'Ubah promo' : 'Promo baru'}
        onClose={() => setForm(null)}
      >
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            <TextField
              label="Nama promo"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              minLength={2}
              autoFocus
            />
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                label="Jenis"
                value={form.kind}
                onChange={(e) => {
                  const kind = e.target.value as PromoForm['kind'];
                  setForm({
                    ...form,
                    kind,
                    scope: kind === 'bogo' && form.scope === 'order' ? 'product' : form.scope,
                    targetIds: [],
                  });
                }}
              >
                {Object.entries(KIND_TEXT).map(([k, t]) => (
                  <option key={k} value={k}>
                    {t}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label="Berlaku untuk"
                value={form.scope}
                onChange={(e) =>
                  setForm({ ...form, scope: e.target.value as PromoForm['scope'], targetIds: [] })
                }
              >
                {Object.entries(SCOPE_TEXT)
                  .filter(([k]) => !(form.kind === 'bogo' && k === 'order'))
                  .map(([k, t]) => (
                    <option key={k} value={k}>
                      {t}
                    </option>
                  ))}
              </SelectField>
            </div>
            {form.kind === 'percent' && (
              <TextField
                label="Persen diskon (%)"
                inputMode="decimal"
                value={form.percent}
                onChange={(e) => setForm({ ...form, percent: e.target.value })}
                required
              />
            )}
            {form.kind === 'amount' && (
              <TextField
                label={
                  form.scope === 'order'
                    ? 'Potongan per transaksi (Rp)'
                    : 'Potongan per unit barang (Rp)'
                }
                inputMode="numeric"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/\D/g, '') })}
                required
              />
            )}
            {form.kind === 'bogo' && (
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label="Beli (unit)"
                  inputMode="numeric"
                  value={form.buyQty}
                  onChange={(e) => setForm({ ...form, buyQty: e.target.value.replace(/\D/g, '') })}
                />
                <TextField
                  label="Gratis (unit)"
                  inputMode="numeric"
                  value={form.getQty}
                  onChange={(e) => setForm({ ...form, getQty: e.target.value.replace(/\D/g, '') })}
                />
              </div>
            )}
            {form.scope !== 'order' && (
              <fieldset>
                <legend className="mb-1 text-sm font-medium">
                  {form.scope === 'product' ? 'Produk sasaran' : 'Kategori sasaran'}
                </legend>
                <ul className="max-h-40 divide-y overflow-auto rounded border text-sm">
                  {targets.map((t) => (
                    <li key={t.id}>
                      <label className="flex items-center gap-2 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={form.targetIds.includes(t.id)}
                          onChange={() =>
                            setForm({ ...form, targetIds: flip(form.targetIds, t.id) })
                          }
                        />
                        {t.name}
                      </label>
                    </li>
                  ))}
                </ul>
              </fieldset>
            )}
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Minimum belanja (Rp)"
                inputMode="numeric"
                value={form.minSpend}
                onChange={(e) => setForm({ ...form, minSpend: e.target.value.replace(/\D/g, '') })}
              />
              <TextField
                label="Batas potongan (Rp, kosong = tanpa batas)"
                inputMode="numeric"
                value={form.maxDiscount}
                onChange={(e) =>
                  setForm({ ...form, maxDiscount: e.target.value.replace(/\D/g, '') })
                }
              />
            </div>
            <fieldset>
              <legend className="mb-1 text-sm font-medium">Hari (kosong = setiap hari)</legend>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d, i) => (
                  <label key={d} className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={form.days.includes(i)}
                      onChange={() => setForm({ ...form, days: flip(form.days, i).sort() })}
                    />
                    {d}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Jam mulai"
                type="time"
                value={form.timeFrom}
                onChange={(e) => setForm({ ...form, timeFrom: e.target.value })}
              />
              <TextField
                label="Jam selesai"
                type="time"
                value={form.timeTo}
                onChange={(e) => setForm({ ...form, timeTo: e.target.value })}
              />
              <TextField
                label="Mulai berlaku"
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
              />
              <TextField
                label="Berakhir"
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
              />
            </div>
            {(outlets.data?.length ?? 0) > 1 && (
              <fieldset>
                <legend className="mb-1 text-sm font-medium">Outlet (kosong = semua)</legend>
                <div className="flex flex-wrap gap-3 text-sm">
                  {outlets.data!.map((o) => (
                    <label key={o.id} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={form.outletIds.includes(o.id)}
                        onChange={() => setForm({ ...form, outletIds: flip(form.outletIds, o.id) })}
                      />
                      {o.name}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Prioritas (besar = lebih dulu)"
                inputMode="numeric"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value.replace(/\D/g, '') })}
              />
              <div className="space-y-1 pt-6 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.stackable}
                    onChange={(e) => setForm({ ...form, stackable: e.target.checked })}
                  />
                  Bisa ditumpuk promo lain
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.requiresVoucher}
                    onChange={(e) => setForm({ ...form, requiresVoucher: e.target.checked })}
                  />
                  Hanya dengan voucher
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                  Aktif
                </label>
              </div>
            </div>
            <div className="text-right">
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ============================ VOUCHER ============================
interface VoucherRow {
  id: string;
  code: string;
  promotionId: string;
  promotionName: string | null;
  quota: number | null;
  usedCount: number;
  expiresAt: string | null;
  active: boolean;
}

export function VouchersPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const write = can('promotions:write');
  const vouchers = useQuery({
    queryKey: ['vouchers'],
    queryFn: () => api.get<VoucherRow[]>('/vouchers'),
  });
  const promos = useQuery({
    queryKey: ['promotions'],
    queryFn: () => api.get<PromoDef[]>('/promotions'),
  });
  const [form, setForm] = useState<{
    code: string;
    promotionId: string;
    quota: string;
    expiresAt: string;
  } | null>(null);
  const save = useSubmit(async () => {
    await api.post('/vouchers', {
      code: form!.code.trim().toUpperCase(),
      promotionId: form!.promotionId,
      quota: form!.quota ? Math.round(Number(form!.quota)) : null,
      expiresAt: form!.expiresAt ? new Date(form!.expiresAt).toISOString() : null,
    });
    inv('vouchers');
    setForm(null);
  });
  const toggle = useSubmit(async (v: VoucherRow) => {
    await api.put(`/vouchers/${v.id}`, { active: !v.active });
    inv('vouchers');
  });
  const cols: Column<VoucherRow>[] = [
    { header: 'Kode', cell: (v) => <code className="font-mono font-medium">{v.code}</code> },
    { header: 'Promo', cell: (v) => v.promotionName ?? '—' },
    {
      header: 'Pemakaian',
      cell: (v) => `${v.usedCount}${v.quota != null ? ` / ${v.quota}` : ' (tanpa batas)'}`,
    },
    { header: 'Kedaluwarsa', cell: (v) => when(v.expiresAt) },
    {
      header: 'Status',
      cell: (v) => (
        <Badge tone={v.active ? 'success' : 'neutral'}>{v.active ? 'Aktif' : 'Mati'}</Badge>
      ),
    },
    {
      header: '',
      className: 'text-right',
      cell: (v) =>
        write && (
          <Button variant="ghost" onClick={() => void toggle.run(v)}>
            {v.active ? 'Matikan' : 'Aktifkan'}
          </Button>
        ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Voucher"
        subtitle="Kode yang mengaktifkan sebuah promo di kasir, dengan kuota dan masa berlaku"
        actions={
          write && (
            <Button
              disabled={(promos.data?.length ?? 0) === 0}
              onClick={() =>
                setForm({
                  code: '',
                  promotionId: promos.data?.[0]?.id ?? '',
                  quota: '',
                  expiresAt: '',
                })
              }
            >
              + Voucher
            </Button>
          )
        }
      />
      {toggle.error && <Alert>{toggle.error}</Alert>}
      {(promos.data?.length ?? 0) === 0 && (
        <Alert tone="info">
          Buat promo dulu di menu Promo, lalu hubungkan dengan kode voucher.
        </Alert>
      )}
      <Card>
        <DataTable columns={cols} rows={vouchers.data ?? []} />
      </Card>
      <Modal open={!!form} title="Voucher baru" onClose={() => setForm(null)}>
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            <TextField
              label="Kode (huruf/angka, - atau _)"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              required
              minLength={3}
              maxLength={30}
              autoFocus
            />
            <SelectField
              label="Promo yang diaktifkan"
              value={form.promotionId}
              onChange={(e) => setForm({ ...form, promotionId: e.target.value })}
            >
              {(promos.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SelectField>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Kuota (kosong = tanpa batas)"
                inputMode="numeric"
                value={form.quota}
                onChange={(e) => setForm({ ...form, quota: e.target.value.replace(/\D/g, '') })}
              />
              <TextField
                label="Kedaluwarsa"
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              />
            </div>
            <div className="text-right">
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ============================ PROGRAM POIN ============================
export function LoyaltyPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const write = can('promotions:write');
  const rule = useQuery({
    queryKey: ['loyalty-rule'],
    queryFn: () => api.get<LoyaltyRule>('/loyalty-rules'),
  });
  const [f, setF] = useState<Record<string, string> | null>(null);
  const cur = f ?? rule.data;
  const form = cur && {
    enabled: cur.enabled as unknown as boolean,
    earnPerAmount: String(cur.earnPerAmount),
    pointsPerUnit: String(cur.pointsPerUnit),
    redeemValue: String(cur.redeemValue),
    minRedeemPoints: String(cur.minRedeemPoints),
  };
  const [saved, setSaved] = useState(false);
  const save = useSubmit(async () => {
    await api.put('/loyalty-rules', {
      enabled: !!form!.enabled,
      earnPerAmount: Math.round(Number(form!.earnPerAmount)),
      pointsPerUnit: Math.round(Number(form!.pointsPerUnit)),
      redeemValue: Math.round(Number(form!.redeemValue)),
      minRedeemPoints: Math.round(Number(form!.minRedeemPoints)),
    });
    inv('loyalty-rule');
    setF(null);
    setSaved(true);
  });
  const set = (k: string, v: string | boolean) => {
    setSaved(false);
    setF({ ...(form as unknown as Record<string, string>), [k]: v as string });
  };
  const earnPreview = form
    ? Math.floor(100_000 / (Number(form.earnPerAmount) || 1)) * (Number(form.pointsPerUnit) || 0)
    : 0;
  return (
    <>
      <PageHeader
        title="Program Poin"
        subtitle="Aturan perolehan dan penukaran poin pelanggan (berlaku untuk seluruh outlet)"
      />
      {form && (
        <Card>
          <form
            className="max-w-lg space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            {saved && <Alert tone="success">Tersimpan.</Alert>}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.enabled}
                disabled={!write}
                onChange={(e) => set('enabled', e.target.checked)}
              />
              Program poin aktif
            </label>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Setiap belanja (Rp)"
                inputMode="numeric"
                value={form.earnPerAmount}
                disabled={!write}
                onChange={(e) => set('earnPerAmount', e.target.value.replace(/\D/g, ''))}
              />
              <TextField
                label="Dapat poin"
                inputMode="numeric"
                value={form.pointsPerUnit}
                disabled={!write}
                onChange={(e) => set('pointsPerUnit', e.target.value.replace(/\D/g, ''))}
              />
              <TextField
                label="Nilai 1 poin (Rp)"
                inputMode="numeric"
                value={form.redeemValue}
                disabled={!write}
                onChange={(e) => set('redeemValue', e.target.value.replace(/\D/g, ''))}
              />
              <TextField
                label="Minimum tukar (poin)"
                inputMode="numeric"
                value={form.minRedeemPoints}
                disabled={!write}
                onChange={(e) => set('minRedeemPoints', e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <p className="text-sm text-slate-600">
              Contoh: belanja {formatRupiah(100_000)} → {earnPreview} poin. Satu poin bernilai{' '}
              {formatRupiah(Number(form.redeemValue) || 0)} saat ditukar. Poin dihitung dari total
              akhir transaksi; pembatalan transaksi membalik poin.
            </p>
            {write && (
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            )}
          </form>
        </Card>
      )}
    </>
  );
}
